import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { EstadoOdontograma } from "app/models/odontograma.interface";
import { POR_DIENTE_QUE_LIMPIAN_CARAS } from "../catalogo-condiciones";
import { OdontogramaConfigService } from "app/services/odontograma-config.service";

type Zona = "top" | "bottom" | "left" | "right" | "center";

interface DienteVisual {
  fdi: string;
  x: number;
  y: number;
  labelY: number;
  zonas: Record<Zona, { fill: string; cara: string }>;
  toothFill: string | null;
  toothOverlay: string | null;
}

const TOOTH = 20;
const TOOTH_SPACING = 25;
const ROW_GAP = 20;
const MIDLINE_GAP = 20;
const LABEL_OFFSET = 10;

const COLOR_DEFAULT = "#ffffff";
const COLOR_ABSENT = "#e0e0e0";

@Component({
  selector: "app-odontograma-chart",
  templateUrl: "./odontograma-chart.component.html",
  styleUrls: ["./odontograma-chart.component.scss"],
})
export class OdontogramaChartComponent implements OnChanges {
  @Input() estado: EstadoOdontograma = {};
  @Input() mode: "edit" | "readonly" = "edit";
  @Input() denticion: "adulto" | "nino" | "mixta" = "adulto";

  @Output() surfaceClick = new EventEmitter<{
    fdi: string;
    cara: string;
    x: number;
    y: number;
  }>();
  @Output() toothLabelClick = new EventEmitter<{ fdi: string }>();

  dientes: DienteVisual[] = [];
  viewBoxWidth = 0;
  viewBoxHeight = 0;

  constructor(private configService: OdontogramaConfigService) {}

  ngOnChanges(_: SimpleChanges): void {
    this.dientes = this.computarDientes();
  }

  caraToZone(fdi: string, cara: string): Zona {
    if (cara === "oclusal") return "center";
    if (cara === "vestibular") return "top";
    if (cara === "lingual") return "bottom";
    const q = fdi[0];
    if (q === "1" || q === "4" || q === "5" || q === "8") {
      return cara === "mesial" ? "right" : "left";
    }
    return cara === "mesial" ? "left" : "right";
  }

  zoneToCara(fdi: string, zone: Zona): string {
    if (zone === "center") return "oclusal";
    if (zone === "top") return "vestibular";
    if (zone === "bottom") return "lingual";
    const q = fdi[0];
    if (q === "1" || q === "4" || q === "5" || q === "8") {
      return zone === "right" ? "mesial" : "distal";
    }
    return zone === "left" ? "mesial" : "distal";
  }

  private colorDeCondicionCara(cod: string): string {
    return this.configService.cara.find((c) => c.codigo === cod)?.color ?? COLOR_DEFAULT;
  }

  private colorDeCondicionDiente(cod: string): string {
    return this.configService.diente.find((c) => c.codigo === cod)?.color ?? COLOR_DEFAULT;
  }

  private filasFDI(): string[][] {
    const permSup = ["18", "17", "16", "15", "14", "13", "12", "11", "21", "22", "23", "24", "25", "26", "27", "28"];
    const permInf = ["48", "47", "46", "45", "44", "43", "42", "41", "31", "32", "33", "34", "35", "36", "37", "38"];
    const tempSup = ["55", "54", "53", "52", "51", "61", "62", "63", "64", "65"];
    const tempInf = ["85", "84", "83", "82", "81", "71", "72", "73", "74", "75"];

    if (this.denticion === "nino") return [tempSup, tempInf];
    if (this.denticion === "mixta") return [permSup, tempSup, tempInf, permInf];
    return [permSup, permInf];
  }

  private computarDientes(): DienteVisual[] {
    const out: DienteVisual[] = [];
    const filas = this.filasFDI();
    const anchoFijo = 16 * TOOTH_SPACING + MIDLINE_GAP;

    filas.forEach((fila, rowIdx) => {
      const mitad = fila.length / 2;
      const anchoFila = fila.length * TOOTH_SPACING + MIDLINE_GAP;
      const offsetX = (anchoFijo - anchoFila) / 2;
      fila.forEach((fdi, colIdx) => {
        const baseX = offsetX + colIdx * TOOTH_SPACING + (colIdx >= mitad ? MIDLINE_GAP : 0);
        const baseY = rowIdx * (TOOTH + LABEL_OFFSET + ROW_GAP);

        const entry = this.estado[fdi];
        const dienteVisual: DienteVisual = {
          fdi,
          x: baseX,
          y: baseY,
          labelY: baseY + TOOTH + 8,
          zonas: this.computarZonas(fdi, entry),
          toothFill: null,
          toothOverlay: null,
        };

        if (entry?.tooth) {
          if (POR_DIENTE_QUE_LIMPIAN_CARAS.includes(entry.tooth)) {
            const c = this.colorDeCondicionDiente(entry.tooth);
            dienteVisual.toothFill = c;
            dienteVisual.zonas = this.zonasUniformes(fdi, COLOR_ABSENT);
            dienteVisual.toothOverlay = entry.tooth === "no_erupcionado" ? null : "X";
          } else {
            dienteVisual.toothOverlay = entry.tooth;
          }
        }

        out.push(dienteVisual);
      });
    });

    this.viewBoxWidth = anchoFijo;
    this.viewBoxHeight = filas.length * (TOOTH + LABEL_OFFSET + ROW_GAP);
    return out;
  }

  private computarZonas(fdi: string, entry: any): Record<Zona, { fill: string; cara: string }> {
    const zonas: Record<Zona, { fill: string; cara: string }> = {
      top: { fill: COLOR_DEFAULT, cara: this.zoneToCara(fdi, "top") },
      bottom: { fill: COLOR_DEFAULT, cara: this.zoneToCara(fdi, "bottom") },
      left: { fill: COLOR_DEFAULT, cara: this.zoneToCara(fdi, "left") },
      right: { fill: COLOR_DEFAULT, cara: this.zoneToCara(fdi, "right") },
      center: { fill: COLOR_DEFAULT, cara: this.zoneToCara(fdi, "center") },
    };

    if (!entry?.surfaces) return zonas;

    for (const [cara, cond] of Object.entries(entry.surfaces)) {
      if (!cond) continue;
      const zone = this.caraToZone(fdi, cara);
      zonas[zone] = { fill: this.colorDeCondicionCara(cond as string), cara };
    }
    return zonas;
  }

  private zonasUniformes(
    fdi: string,
    fill: string,
  ): Record<Zona, { fill: string; cara: string }> {
    return {
      top: { fill, cara: this.zoneToCara(fdi, "top") },
      bottom: { fill, cara: this.zoneToCara(fdi, "bottom") },
      left: { fill, cara: this.zoneToCara(fdi, "left") },
      right: { fill, cara: this.zoneToCara(fdi, "right") },
      center: { fill, cara: this.zoneToCara(fdi, "center") },
    };
  }

  onZoneClick(event: MouseEvent, diente: DienteVisual, zone: Zona): void {
    if (this.mode === "readonly") return;
    event.stopPropagation();
    this.surfaceClick.emit({
      fdi: diente.fdi,
      cara: this.zoneToCara(diente.fdi, zone),
      x: event.clientX,
      y: event.clientY,
    });
  }

  onLabelClick(event: MouseEvent, diente: DienteVisual): void {
    if (this.mode === "readonly") return;
    event.stopPropagation();
    this.toothLabelClick.emit({ fdi: diente.fdi });
  }

  trackByFdi(_: number, d: DienteVisual): string {
    return d.fdi;
  }
}
